import { useEffect, useState, type FormEvent } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
  ArrowLeft, Eye, EyeOff, Lock, Mail, ShieldCheck, AlertTriangle,
  FileText, UserCheck, ClipboardList, CheckCircle2, MailCheck,
  UserPlus, Image as ImageIcon, FileType, BookOpen, ScrollText, Info,
} from "lucide-react"
import { useAuth } from "../../context/auth-context"
import { ApiError } from "../../lib/api"
import { SCHOOL } from "../../lib/constants"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Alert } from "../../components/ui/Alert"
import { Badge } from "../../components/ui/Badge"
import { Dialog } from "../../components/ui/Dialog"
import { Checkbox } from "../../components/ui/Checkbox"
import { ThemeToggle } from "../../components/ui/ThemeToggle"

const GUIDELINES_KEY = "yms-guidelines-seen"

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

const STEPS = [
  {
    icon: UserPlus,
    title: "Create Account",
    text: "Register with a valid email and password to start your application journey.",
  },
  {
    icon: ClipboardList,
    title: "Complete Application Form",
    text: "Fill out the NCE application form with personal, JAMB, O-Level and guardian details.",
  },
  {
    icon: FileText,
    title: "Admin Reviews",
    text: "Our admissions team carefully reviews your application and uploaded documents.",
  },
  {
    icon: MailCheck,
    title: "Approve / Reject Email",
    text: "You will receive an email notifying you of the decision — approved or rejected with reason.",
  },
  {
    icon: UserCheck,
    title: "Activate Student Portal",
    text: "Approved applicants receive matriculation numbers and full portal access.",
  },
]

const DOCUMENTS = [
  {
    icon: ImageIcon,
    name: "Passport Photograph",
    spec: "JPEG or PNG • Max 500KB",
    note: "Recent clear passport-size photograph on plain background.",
  },
  {
    icon: ScrollText,
    name: "Birth Certificate",
    spec: "PDF • Max 2MB",
    note: "Official birth certificate or sworn declaration of age.",
  },
  {
    icon: BookOpen,
    name: "O'Level Result",
    spec: "PDF • Max 2MB",
    note: "WAEC, NECO or NABTEB with at least 5 credits including English & Maths.",
  },
  {
    icon: ScrollText,
    name: "JAMB Result",
    spec: "PDF • Max 2MB",
    note: "Current JAMB UTME result slip showing your score and subjects.",
  },
]

function GuidelinesDialog({
  open,
  onClose,
  onAgree,
  agreed,
  setAgreed,
}: {
  open: boolean
  onClose: () => void
  onAgree: () => void
  agreed: boolean
  setAgreed: (v: boolean) => void
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Registration Guidelines"
      className="max-w-2xl"
    >
      <div className="space-y-6">
        <div className="rounded-lg bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-brand-700 dark:text-brand-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-brand-900 dark:text-brand-200">
            Please read these guidelines carefully. You must acknowledge that you have read and
            understood them before you can create your account.
          </p>
        </div>

        {/* 5-step process */}
        <div>
          <h3 className="font-display font-semibold text-ink-900 dark:text-ink-100 mb-3">
            Application Process — 5 Steps
          </h3>
          <ol className="space-y-3">
            {STEPS.map((step, i) => {
              const Icon = step.icon
              return (
                <li key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-brand-800 text-white grid place-items-center font-bold text-sm flex-shrink-0">
                      {i + 1}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="w-px flex-1 bg-ink-200 dark:bg-ink-700 my-1" />
                    )}
                  </div>
                  <div className="pb-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Icon className="w-4 h-4 text-brand-700 dark:text-brand-400" />
                      <span className="font-semibold text-sm text-ink-900 dark:text-ink-100">
                        {step.title}
                      </span>
                    </div>
                    <p className="text-xs text-ink-600 dark:text-ink-400 leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>

        {/* Required documents */}
        <div>
          <h3 className="font-display font-semibold text-ink-900 dark:text-ink-100 mb-3">
            Required Documents
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {DOCUMENTS.map((doc, i) => {
              const Icon = doc.icon
              return (
                <div
                  key={i}
                  className="rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 p-3"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-4 h-4 text-gold-600 dark:text-gold-400 flex-shrink-0" />
                    <span className="font-semibold text-sm text-ink-900 dark:text-ink-100">
                      {doc.name}
                    </span>
                  </div>
                  <Badge variant="warning" className="mb-1.5">{doc.spec}</Badge>
                  <p className="text-xs text-ink-600 dark:text-ink-400 leading-relaxed">
                    {doc.note}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        <div className="rounded-lg bg-gold-50 dark:bg-gold-900/30 border border-gold-200 dark:border-gold-800 p-3">
          <p className="text-xs text-gold-900 dark:text-gold-200 leading-relaxed">
            <strong>Important:</strong> All document uploads must be clear, legible and unaltered.
            Forged or altered documents will lead to automatic disqualification and may be reported
            to the appropriate authorities.
          </p>
        </div>

        <div className="border-t border-ink-200 dark:border-ink-800 pt-4 space-y-4">
          <Checkbox
            id="agree-guidelines"
            checked={agreed}
            onChange={setAgreed}
            label="I have read and understood the registration guidelines and document requirements."
          />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={onClose} type="button">
              Close
            </Button>
            <Button onClick={onAgree} disabled={!agreed} type="button">
              <CheckCircle2 className="w-4 h-4" />
              I Agree — Continue
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default function PortalRegister() {
  const navigate = useNavigate()
  const { register } = useAuth()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [guidelinesOpen, setGuidelinesOpen] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [hasAgreedFinal, setHasAgreedFinal] = useState(false)

  // Show guidelines automatically on first visit
  useEffect(() => {
    try {
      const seen = sessionStorage.getItem(GUIDELINES_KEY)
      if (!seen) {
        setGuidelinesOpen(true)
      } else {
        // Restore prior agreement in the same browser session
        setHasAgreedFinal(true)
      }
    } catch {
      // sessionStorage might be unavailable; default to showing dialog
      setGuidelinesOpen(true)
    }
  }, [])

  function handleAgreeFromDialog() {
    if (!agreed) return
    try { sessionStorage.setItem(GUIDELINES_KEY, "1") } catch {}
    setHasAgreedFinal(true)
    setGuidelinesOpen(false)
    toast.success("Thank you. You can now create your account.")
  }

  function handleCloseDialog() {
    setGuidelinesOpen(false)
    // Persist the dialog dismiss only if user already agreed previously
    if (!hasAgreedFinal) {
      try { sessionStorage.setItem(GUIDELINES_KEY, "1") } catch {}
    }
  }

  function openGuidelines() {
    setGuidelinesOpen(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!hasAgreedFinal) {
      setError("Please read and accept the registration guidelines first.")
      setGuidelinesOpen(true)
      return
    }
    if (!email || !password || !confirmPassword) {
      setError("Please fill in all fields.")
      return
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }
    setLoading(true)
    try {
      await register(email, password)
      toast.success("Account created! Redirecting to application form...")
      navigate("/apply")
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Registration failed. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  function handleGoogle() {
    window.location.href = "/api/auth/google?returnTo=/apply"
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-ink-50 dark:bg-ink-950">
      {/* Branding panel */}
      <aside className="relative md:w-1/2 lg:w-3/5 bg-gradient-to-br from-brand-800 via-brand-900 to-brand-950 text-white overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-30" aria-hidden="true" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl" aria-hidden="true" />
        <div className="relative h-full flex flex-col p-8 md:p-12 lg:p-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={`${SCHOOL.shortName} logo`} className="w-12 h-12 rounded-lg bg-white/95 p-1.5" />
            <div>
              <div className="font-display font-bold text-lg leading-tight">{SCHOOL.shortName}</div>
              <div className="text-xs text-white/70">Admissions Portal</div>
            </div>
          </div>

          <div className="mt-auto max-w-md">
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Begin your <span className="text-gradient-gold">teaching career</span> with us
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6">
              Create your account to apply for the Nigeria Certificate in Education (NCE) programme
              at {SCHOOL.fullName}.
            </p>
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> NCCE-accredited programmes</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> Affordable tuition & flexible payment</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> Modern facilities & experienced faculty</li>
            </ul>
          </div>

          <div className="mt-10 text-xs text-white/60">
            © {new Date().getFullYear()} {SCHOOL.fullName}. All rights reserved.
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-brand-700 dark:hover:text-brand-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="md:hidden flex items-center gap-3 mb-6 justify-center">
              <img src="/logo.png" alt={`${SCHOOL.shortName} logo`} className="w-10 h-10 rounded-lg" />
              <div className="font-display font-bold text-ink-900 dark:text-ink-100">{SCHOOL.shortName}</div>
            </div>

            <div className="mb-5">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 dark:text-ink-100 mb-1">
                Create Student Account
              </h2>
              <p className="text-sm text-ink-500 dark:text-ink-400">
                Already have an account?{" "}
                <Link to="/portal/login" className="font-semibold text-brand-700 dark:text-brand-400 hover:underline">
                  Login
                </Link>
              </p>
            </div>

            {/* Guidelines status banner */}
            <button
              type="button"
              onClick={openGuidelines}
              className={`w-full text-left rounded-lg border p-3 mb-4 flex items-start gap-2.5 transition-colors ${
                hasAgreedFinal
                  ? "border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/30"
                  : "border-gold-200 dark:border-gold-800 bg-gold-50 dark:bg-gold-900/30"
              }`}
            >
              {hasAgreedFinal ? (
                <CheckCircle2 className="w-5 h-5 text-brand-700 dark:text-brand-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-gold-600 dark:text-gold-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${hasAgreedFinal ? "text-brand-900 dark:text-brand-200" : "text-gold-900 dark:text-gold-200"}`}>
                  {hasAgreedFinal ? "Guidelines accepted" : "Registration guidelines required"}
                </div>
                <div className="text-xs text-ink-600 dark:text-ink-400 mt-0.5">
                  {hasAgreedFinal
                    ? "You may proceed with creating your account."
                    : "Please review and accept the guidelines to continue."}{" "}
                  <span className="font-semibold text-brand-700 dark:text-brand-400 underline">View Guidelines</span>
                </div>
              </div>
            </button>

            {error && (
              <Alert variant="danger" className="mb-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="pl-9 bg-white dark:bg-ink-900"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Min. 8 characters"
                    className="pl-9 pr-9 bg-white dark:bg-ink-900"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 dark:hover:text-ink-200"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    className="pl-9 bg-white dark:bg-ink-900"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">Passwords do not match.</p>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={loading || !hasAgreedFinal}
              >
                {loading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                    Creating account...
                  </>
                ) : "Create Account"}
              </Button>
            </form>

            {!hasAgreedFinal && (
              <p className="mt-3 text-xs text-center text-ink-500 dark:text-ink-400">
                <Lock className="w-3 h-3 inline mr-1" />
                Accept the registration guidelines to enable account creation.
              </p>
            )}

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-ink-200 dark:bg-ink-800" />
              <span className="text-xs uppercase tracking-wider text-ink-400">or</span>
              <div className="flex-1 h-px bg-ink-200 dark:bg-ink-800" />
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full bg-white dark:bg-ink-900"
              onClick={handleGoogle}
              disabled={loading}
            >
              <GoogleIcon />
              Sign up with Google
            </Button>

            <p className="mt-6 text-xs text-ink-500 dark:text-ink-400 text-center leading-relaxed">
              By creating an account, you consent to our{" "}
              <Link to="/terms" className="underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </main>

      <GuidelinesDialog
        open={guidelinesOpen}
        onClose={handleCloseDialog}
        onAgree={handleAgreeFromDialog}
        agreed={agreed}
        setAgreed={setAgreed}
      />
    </div>
  )
}
